import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Key, Building2, Users, CheckCircle2, AlertTriangle, Crown } from 'lucide-react';
import { toast } from 'sonner';

const LicenseTiersPage = () => {
  const [loading, setLoading] = useState(true);
  const [licenses, setLicenses] = useState([]);
  const [tenantUsage, setTenantUsage] = useState([]);

  const defaultLicenses = [
    {
      id: 'free',
      name: 'Free',
      description: 'Basic features for small teams',
      features: ['5 users', 'OSINT module only', '1 cloud account', 'Community support'],
      price: 0,
      tenantsCount: 45,
      color: 'slate'
    },
    {
      id: 'starter',
      name: 'Starter',
      description: 'Essential security for growing teams',
      features: ['25 users', 'OSINT + GRC basics', '3 cloud accounts', 'Email support', 'PDF reports'],
      price: 99,
      tenantsCount: 28,
      color: 'blue'
    },
    {
      id: 'professional',
      name: 'Professional',
      description: 'Advanced features for security teams',
      features: ['100 users', 'All modules', '10 cloud accounts', 'Priority support', 'API access', 'Custom frameworks'],
      price: 299,
      tenantsCount: 15,
      color: 'purple'
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      description: 'Full platform for large organizations',
      features: ['Unlimited users', 'All modules', 'Unlimited accounts', '24/7 support', 'SSO/SAML', 'SLA guarantee', 'Dedicated CSM'],
      price: 'Custom',
      tenantsCount: 8,
      color: 'amber'
    }
  ];

  const sampleTenantUsage = [
    { name: 'Beskar Industries', license: 'enterprise', users: 156, usersLimit: 'Unlimited', accounts: 12, accountsLimit: 'Unlimited', status: 'active' },
    { name: 'Acme Corp', license: 'professional', users: 78, usersLimit: 100, accounts: 8, accountsLimit: 10, status: 'active' },
    { name: 'TechStart Inc', license: 'starter', users: 22, usersLimit: 25, accounts: 3, accountsLimit: 3, status: 'warning' },
    { name: 'DevOps Labs', license: 'starter', users: 15, usersLimit: 25, accounts: 2, accountsLimit: 3, status: 'active' },
    { name: 'CloudFirst', license: 'professional', users: 45, usersLimit: 100, accounts: 6, accountsLimit: 10, status: 'active' },
  ];

  useEffect(() => {
    setTimeout(() => {
      setLicenses(defaultLicenses);
      setTenantUsage(sampleTenantUsage);
      setLoading(false);
    }, 500);
  }, []);

  const getColorClasses = (color) => {
    const colors = {
      slate: 'bg-slate-100 border-slate-200 text-slate-700',
      blue: 'bg-blue-100 border-blue-200 text-blue-700',
      purple: 'bg-purple-100 border-purple-200 text-purple-700',
      amber: 'bg-amber-100 border-amber-200 text-amber-700'
    };
    return colors[color] || colors.slate;
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-64" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="license-tiers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Key className="w-7 h-7 text-green-500" />
            License Tiers
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage subscription plans and tenant allocations
          </p>
        </div>
      </div>

      {/* License Tiers */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {licenses.map((license) => (
          <Card key={license.id} className={`border-2 ${getColorClasses(license.color)}`} data-testid={`license-${license.id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{license.name}</CardTitle>
                {license.id === 'enterprise' && <Crown className="w-5 h-5 text-amber-500" />}
              </div>
              <CardDescription className="text-xs">{license.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="text-2xl font-bold">
                  {typeof license.price === 'number' ? `$${license.price}/mo` : license.price}
                </div>
                <ul className="space-y-1 text-sm">
                  {license.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle2 className="w-3 h-3 text-green-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="pt-2 border-t">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1">
                      <Building2 className="w-4 h-4" />
                      Tenants
                    </span>
                    <Badge variant="outline">{license.tenantsCount}</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tenant Usage Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Tenant License Usage</CardTitle>
          <CardDescription>Monitor resource consumption across tenants</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tenant</TableHead>
                <TableHead>License</TableHead>
                <TableHead>Users</TableHead>
                <TableHead>Cloud Accounts</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenantUsage.map((tenant, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{tenant.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">{tenant.license}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span>{tenant.users} / {tenant.usersLimit}</span>
                      </div>
                      {typeof tenant.usersLimit === 'number' && (
                        <Progress value={(tenant.users / tenant.usersLimit) * 100} className="h-1" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span>{tenant.accounts} / {tenant.accountsLimit}</span>
                      </div>
                      {typeof tenant.accountsLimit === 'number' && (
                        <Progress value={(tenant.accounts / tenant.accountsLimit) * 100} className="h-1" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {tenant.status === 'active' ? (
                      <Badge variant="default" className="bg-green-500">Active</Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Near Limit
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default LicenseTiersPage;
